import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { SocieteSelector } from './SocieteSelector';
import { useValidationCount } from '@/hooks/useCommercial';
import { BPE_EN_SERVICE } from '@/constants/activites';
import { COMMON_SECTIONS, HOLDING_NAV, METIER_NAV, MON_ESPACE_NAV, TERRAIN_ADMIN_NAV, type NavItem, type NavSection } from '@/constants/metierNav';

/**
 * Sidebar pixel-perfect CTO-FRONT-002.
 * - Brand block : gradient var(--p-deep) → var(--p-dark) + cercles décoratifs
 * - Nav links : .ds-nav-link, .on = gradient var(--p) → var(--p-light)
 * - User block : avatar gradient + nom + rôle + bouton logout
 */
export const Sidebar = () => {
  const { user, filiale, logout } = useAuthStore();
  const navigate = useNavigate();
  const [logoFailed, setLogoFailed] = useState(false);
  if (!user) return null;

  // Garde-fou : si le JWT n'a pas posé user.role (hook cassé / user supprimé),
  // la sidebar serait vide silencieusement. On force un re-login explicite.
  if (!user.role) {
    return (
      <aside className="ds-sidebar" style={{ padding: 20 }}>
        <div className="ds-brand-name">Session expirée</div>
        <p style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 8 }}>
          Votre token de connexion est invalide. Veuillez vous reconnecter.
        </p>
        <button type="button" className="ds-btn ds-btn-p" style={{ marginTop: 12 }} onClick={() => void logout()}>
          Se reconnecter
        </button>
      </aside>
    );
  }

  const canSeeHolding = user.role === 'admin_holding' || user.role === 'auditeur';
  const metierSections = filiale ? METIER_NAV[filiale.metier] : [];
  // Portail Terrain n'est pertinent que pour le métier béton (LeadBeton CI).
  // Les rôles habilités (admin_holding, daf, dg_filiale, manager) filtrent les items.
  const canSeeTerrainAdmin = filiale?.metier === 'beton';

  // Les sections communes sont désormais au nombre de trois — ce qui attend
  // une décision, la finance, les ressources — et chaque métier en apporte
  // plusieurs au lieu d'une seule longue liste.
  const sections: NavSection[] = [
    ...COMMON_SECTIONS,
    ...metierSections.filter((s) => s.items.length > 0),
    ...(canSeeTerrainAdmin ? [TERRAIN_ADMIN_NAV] : []),
    ...(canSeeHolding ? [HOLDING_NAV] : []),
    // En dernier, juste au-dessus du bloc utilisateur : un réglage personnel
    // se range près de son propriétaire, pas au milieu des modules métier.
    MON_ESPACE_NAV,
  ];

  const initials = user.fullName
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <aside className="ds-sidebar">
      {/* ── Brand block (FIXE en haut) ───────────────────── */}
      <div className="ds-brand">
        <div className="ds-brand-logo">
          {filiale && !logoFailed ? (
            <img
              src={filiale.logoPath.startsWith('/') ? filiale.logoPath : `/${filiale.logoPath}`}
              alt={filiale.name}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div className="ds-brand-logo-fallback">
              {filiale?.name ?? user.filialeName ?? user.filialeCode}
            </div>
          )}
        </div>
        <div className="ds-brand-name">{filiale?.name ?? user.filialeName}</div>
        <div className="ds-brand-sub">
          {filiale ? `${filiale.pays} · ${filiale.devise}` : user.filialeCode}
        </div>
        {filiale?.metierLabel && (
          <span className="ds-brand-pill">{filiale.metierLabel.toUpperCase()}</span>
        )}
        <SocieteSelector />
      </div>

      {/* ── Nav sections (DÉFILE) ────────────────────────── */}
      <div className="ds-nav-scroll">
        <nav style={{ paddingBottom: 8 }}>
          {sections.map((section) => {
            const visibleItems = section.items.filter((item: NavItem) =>
              (!item.roles || item.roles.includes(user.role)) && (!item.bpe || BPE_EN_SERVICE));
            // Cache la section entière si aucun item n'est visible pour ce rôle (évite headers vides)
            if (visibleItems.length === 0) return null;
            return (
            <div key={section.title}>
              <div className="ds-nav-section">{section.title}</div>
              {visibleItems
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/finance' || item.path === '/operations' || item.path === '/crm'}
                      className={({ isActive }) => `ds-nav-link${isActive ? ' on' : ''}`}
                    >
                      <span className="ds-nav-ico">
                        <Icon size={16} />
                      </span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.path === '/validation' && <ValidationCountBadge />}
                    </NavLink>
                  );
                })}
            </div>
            );
          })}
        </nav>
      </div>

      {/* Note : le badge est monté DANS la NavLink pour bénéficier de la mise
          en page flex existante. Il ne prend pas de place tant qu'il n'y a rien
          à valider (renvoie null) — visuellement neutre. */}

      {/* ── User block (FIXE en bas) ─────────────────────────
          Le bloc entier déconnectait au clic : un geste imprécis en bas de
          l'écran vous mettait dehors, sans confirmation. Il mène désormais à
          « Mon compte » — où l'on change son mot de passe — et la
          déconnexion reste sur son bouton, qui arrête la propagation. */}
      <div
        className="ds-user-block"
        onClick={() => navigate('/mon-compte')}
        role="button"
        tabIndex={0}
        title="Mon compte"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/mon-compte'); }}
      >
        <div className="ds-user-av">{initials}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="ds-user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.fullName}
          </div>
          <div className="ds-user-role">{user.role}</div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); void logout(); }}
          className="ds-user-logout"
          aria-label="Se déconnecter"
          title="Se déconnecter"
        >
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
};

/**
 * SIMG-COMMERCIAL/ACHATS · Badge dynamique sur l'item "Validations".
 * Hook TanStack Query avec refetchInterval 60s (défini dans useValidationCount).
 * Rendu discret (rond rouge) — masqué quand total = 0 pour ne pas polluer.
 */
function ValidationCountBadge() {
  const { data } = useValidationCount();
  const total = data?.total ?? 0;
  if (total <= 0) return null;
  return (
    <span
      title={`${total} pièce(s) en attente du DAF${data?.indisponibles ? " — certaines files illisibles" : ""}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999,
        background: '#DC2626', color: '#fff', fontSize: 10, fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {total > 99 ? '99+' : total}
    </span>
  );
}
